import {
  projectMembershipSeatAuthority,
  type MembershipPlanCapacity,
  type MembershipSeatAuthorityReport,
} from '@/governor/membership/MembershipSeatAuthority';

export interface GovernorMembershipObservation {
  timestamp: string;
  subsystem: 'Membership';
  status: 'HEALTHY' | 'WARNING' | 'FAILURE';
  observation: string;
  evidence?: string;
}

export interface GovernorMembershipAgentInput {
  planCapacity?: MembershipPlanCapacity;
  activeSponsoredSeats?: number;
  reservedSponsoredSeats?: number;
  returnedSeatCount?: number;
  familyInvitesSent?: number;
  familyInvitesPending?: number;
}

export interface GovernorMembershipAgentResult {
  seatAuthority: MembershipSeatAuthorityReport;
  observations: readonly GovernorMembershipObservation[];
}

export function runGovernorMembershipAgent(
  input: GovernorMembershipAgentInput = {},
): GovernorMembershipAgentResult {
  const seatAuthority = projectMembershipSeatAuthority({
    planCapacity: input.planCapacity ?? 1,
    activeSponsoredSeats:
      input.activeSponsoredSeats ?? 0,
    reservedSponsoredSeats:
      input.reservedSponsoredSeats ?? 0,
    returnedSeatCount:
      input.returnedSeatCount ?? 0,
    familyInvitesSent:
      input.familyInvitesSent ?? 0,
    familyInvitesPending:
      input.familyInvitesPending ?? 0,
  });

  const observationStatus:
    GovernorMembershipObservation['status'] =
      seatAuthority.status === 'INVALID'
        ? 'FAILURE'
        : seatAuthority.status === 'FULL'
          ? 'WARNING'
          : 'HEALTHY';

  const observations = Object.freeze([
    Object.freeze({
      timestamp: new Date().toISOString(),
      subsystem: 'Membership' as const,
      status: observationStatus,
      observation:
        'Membership seat and invitation authority evaluated.',
      evidence: seatAuthority.summary,
    }),
  ]);

  return Object.freeze({
    seatAuthority,
    observations,
  });
}
